import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import {
	ColumnDef,
	ExpandedState,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	Row,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import cn from 'classnames';

import {
	Button,
	Datepicker,
	DynamicIconWrapper,
	Icons,
	Select,
	SpinnerOverlay,
	Tooltip,
} from '@components/common';
import ActionsMenu from '@components/common/ActionsMenu';
import Label from '@components/common/Label';
import Pagination from '@components/common/Pagination';
import OptionTruncationOnly from '@components/common/Select/components/OptionTruncationOnly';
import MultiSearch from '@components/common/Table/MultiSearch';
import { IAssetVisibilityData } from '@components/routes/AdminPanel/AssetsList/AssetsList';

import Refresh from '@icons/collection/Refresh';
import { TIconType } from '@icons/interface';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { useAppDispatch } from '@store/store';

import { isEmpty } from '@utils/common';
import {
	getIdsGroupAction,
	modifyMenuActionsBasedOnFilters,
	modifyMenuActionsBasedOnSelection,
	modifyMenuActionsTextBasedOnCount,
} from '@utils/table/groupActionUtil';
import { checkIsAdmin, checkIsPlatformOwner } from '@utils/users';

import { TAsset } from '@interfaces/assets/asset';
import { IGroupOperationWithActionDTO, TFoldersId } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	IActionMenu,
	IMultiDataTableFilter,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { TRole } from '@interfaces/user/user';

import { EMenuActionsTypes, TMenuActionsTypes } from '@enums/common';

import colors from '@constants/colors';
import { EXPORT_MENU_MIN_WIDTH, importSitesActions, menuActions } from '@constants/common';
import { TableFilterTypes } from '@constants/table';
import {
	assetVisibilityTooltipText,
	DEFAULT_DRAG_HANDLE_SIZE,
	deleteSelectedTooltipText,
	DRAG_HANDLE_COLUMN_ID,
	exportTooltipDefaultText,
	filterTooltipText,
	pinColumns,
	refreshAction,
	resetFilterTooltipText,
	selectColumn,
	setCommonValuesTooltipText,
	setExportRulesTooltipText,
	unlinkSelectedTooltipText,
	unpinColumns,
	verifyCoordinatesSelectedTooltipText,
} from '@components/common/Table/constants';

import { getRowItemId } from 'components/common/Table/helpers';

import AssetVisibilityActionsMenu from './AssetVisibilityActionsMenu/AssetVisibilityActionsMenu';
import MultiDataFilter from '../MultiDataFilter/MultiDataFilter';
import DragDropTableWrapper from './DragDropTableWrapper';
import SortableRow from './SortableRow';

import styles from './Table.module.scss';

interface ITableProps {
	addIcon?: TIconType;
	addText?: string;
	addTooltipText?: string;
	className?: string;
	columns: ColumnDef<any>[];
	collapseTriggerNames?: string[];
	commonValuesActions?: IActionMenu<TMenuActionsTypes>[];
	customExportActions?: IActionMenu<TMenuActionsTypes>[];
	customLeftAction?: React.ReactNode | null;
	customAttachActions?: IActionMenu<TMenuActionsTypes>[];
	customRemoveActions?: IActionMenu<TMenuActionsTypes>[];
	customRightAction?: React.ReactNode | null;
	data: Array<any>;
	dateFilters?: Array<ITableDatepickerFilter>;
	emptyText?: string;
	exportTooltipText?: string;
	moveItemsTooltipText?: string;
	filters?: Array<TTableFilter>;
	tableName?: string;
	handleChangeCommonValues?: (ids: Array<any>, actionId: TMenuActionsTypes) => void;
	handleCustomDelete?: (data: IGroupOperationWithActionDTO) => void;
	handleCustomExport?: () => void;
	handleDelete?: () => void;
	handleExport?: (ids: Array<any>, actionId: TMenuActionsTypes) => void;
	handleAttach?: (data: IGroupOperationWithActionDTO) => void;
	handleExportRules?: () => void;
	handleFilter?: () => void;
	handleImport?: (actionId?: string) => void;
	handleRefresh?: () => void;
	handleResetFilters?: () => void;
	handleSearch?: () => void;
	handleUnlink?: () => void;
	handleVisibilityAction?: (data: IAssetVisibilityData) => void;
	handleMoveItems?: ({ ids }: {ids: Array<any>}) => void;
	importTooltipText?: string;
	isAddDisabled?: boolean;
	isAttach?: boolean;
	isAttachDisabled?: boolean;
	attachTooltipText?: string;
	isCreatable?: boolean;
	isEditAccess?: boolean;
	isEmptyTextCenter?: boolean;
	isExport?: boolean;
	isMoveItems?: boolean;
	isExportSettings?: boolean;
	isImportSettings?: boolean;
	isImportable?: boolean;
	isLink?: boolean;
	isClearableDatepicker?: boolean;
	isLinkAccess?: boolean;
	isLoading: boolean;
	isOpenFilters?: boolean | null;
	isOpenSearch?: boolean;
	isPinColumns?: boolean;
	isRefreshTable?: boolean;
	isSetCommonValues?: boolean;
	isSearchDisabled?: boolean;
	isToggleAssetVisibility?: boolean;
	isVerifyCoordinates?: boolean;
	isWithPagination?: boolean;
	isWithoutActions?: boolean;
	isWithoutCheckbox?: boolean;
	isWithoutDeleteAction?: boolean;
	isWithoutDeleteActionMenu?: boolean;
	isWithoutFilter?: boolean;
	keysForCheckedIds?: Array<string>;
	loadingComponent?: React.ReactNode;
	maxHeight?: string;
	height?: string;
	onScrollHandler?: (e: any) => void;
	pagination?: ITablePagination;
	search?: Array<ITableSearch>;
	searchThreshold?: number;
	searchWidth?: string;
	setCheckedIds?: React.Dispatch<React.SetStateAction<string[]>>;
	setSelectedIds?: React.Dispatch<React.SetStateAction<string[]>>;
	sort?: ITableSort;
	stickyLeftColumnIndexes?: number[];
	toggleAddModal?: () => void;
	toggleDeleteModal?: (isAdminExist?: boolean) => void;
	toggleUnlinkModal?: () => void;
	verifyCoordinates?: (data: string[]) => void;
	isDeleteActionDisabled?: boolean;
	isCustomActionInGroupOperations?: boolean;
	isDragAndDrop?: boolean;
	onRowReorder?: (oldIndex: number, newIndex: number) => void;
	dragHandle?: boolean;
	dragHandleColumn?: ColumnDef<any>;
	getRowId?: (originalRow: any, index: number) => string;
	multiDataFilters?: IMultiDataTableFilter;
}

const Table = ({
	addIcon = Icons.plus,
	addText = 'Add new',
	addTooltipText,
	className,
	columns,
	collapseTriggerNames = [],
	customAttachActions = menuActions,
	commonValuesActions = menuActions,
	customExportActions = menuActions,
	customLeftAction = null,
	customRemoveActions = menuActions,
	customRightAction = null,
	data,
	dateFilters = [],
	emptyText = 'No results.',
	exportTooltipText,
	filters = [],
	tableName,
	handleAttach,
	handleChangeCommonValues,
	handleCustomDelete,
	handleCustomExport,
	handleExport,
	handleExportRules,
	handleImport,
	handleRefresh,
	handleResetFilters,
	handleVisibilityAction,
	handleMoveItems,
	setSelectedIds,
	importTooltipText = 'Import',
	isAddDisabled,
	isAttach,
	isAttachDisabled,
	attachTooltipText,
	isCreatable = false,
	isSearchDisabled,
	isEditAccess = true,
	isEmptyTextCenter = false,
	isExport,
	isMoveItems,
	isClearableDatepicker = true,
	isExportSettings = false,
	isImportSettings = false,
	isImportable = false,
	isLink = false,
	isLinkAccess,
	isLoading,
	isOpenFilters = false,
	isOpenSearch = false,
	isPinColumns = false,
	isRefreshTable = false,
	isSetCommonValues = false,
	isToggleAssetVisibility,
	isVerifyCoordinates = false,
	isWithPagination = true,
	isWithoutActions = false,
	isWithoutCheckbox = false,
	isWithoutDeleteAction = false,
	isWithoutDeleteActionMenu,
	isWithoutFilter = false,
	isDeleteActionDisabled,
	keysForCheckedIds = ['id'],
	loadingComponent: LoadingComponent,
	maxHeight = 'none',
	height,
	onScrollHandler,
	pagination,
	search,
	searchThreshold,
	searchWidth,
	setCheckedIds,
	sort = { initialState: [], handleSort: () => null },
	stickyLeftColumnIndexes,
	toggleAddModal,
	toggleDeleteModal,
	toggleUnlinkModal,
	verifyCoordinates,
	isCustomActionInGroupOperations,
	isDragAndDrop = false,
	onRowReorder,
	dragHandle = true,
	dragHandleColumn,
	getRowId = getRowItemId,
	multiDataFilters,
	moveItemsTooltipText,
}: ITableProps) => {
	const dispatch = useAppDispatch();

	const columnsFormatted = useMemo(() => {
		return (isWithoutCheckbox || !isEditAccess)
			? columns
			: [...selectColumn, ...columns];
	}, [isWithoutCheckbox, isEditAccess, columns]);

	const defaultDragHandleColumn: ColumnDef<any> = useMemo(() => ({
		id: DRAG_HANDLE_COLUMN_ID,
		header: '',
		size: DEFAULT_DRAG_HANDLE_SIZE,
		enableSorting: false,
		enableResizing: false,
		cell: () => {
			return null;
		},
	}), []);

	const finalColumns = useMemo(() => {
		if (!isDragAndDrop || !dragHandle) {
			return columnsFormatted;
		}

		const handleColumn = dragHandleColumn || defaultDragHandleColumn;

		return [handleColumn, ...columnsFormatted];
	}, [isDragAndDrop, dragHandle, dragHandleColumn, defaultDragHandleColumn, columnsFormatted]);

	const [sorting, setSorting] = useState<SortingState>(sort.initialState as SortingState);
	const [expanded, setExpanded] = useState<ExpandedState>({});

	const tableRef: React.Ref<HTMLTableElement> = useRef(null);
	const tHeadTrRef: React.Ref<HTMLTableRowElement> = useRef(null);
	const tBodyTrRef: React.Ref<HTMLTableRowElement> = useRef(null);

	const scrollWrapperRef = useRef<HTMLInputElement | null>(null);
	const actionAttachMenuWrapperRef = useRef<HTMLDivElement | null>(null);
	const actionExportMenuWrapperRef = useRef<HTMLDivElement | null>(null);
	const actionDeleteMenuWrapperRef = useRef<HTMLDivElement | null>(null);
	const actionCommonValuesMenuWrapperRef = useRef<HTMLDivElement | null>(null);
	const actionImportMenuWrapperRef = useRef<HTMLDivElement | null>(null);
	const actionAssetVisibilityMenuWrapperRef = useRef<HTMLDivElement | null>(null);

	const isEmptyData = !data?.length;
	const sortId = sort.initialState[0];

	const isDisabledClearFilters = useMemo(() => {
		return !!filters?.every(({ option }) => Array.isArray(option) ? !option.length : !option)
			&& !!dateFilters?.every(({ selected }) => !selected) && multiDataFilters?.isEmptyFilter;
	}, [filters, dateFilters, multiDataFilters]);

	const [isShowFilters, setIsShowFilters] = useState(isOpenFilters);
	const [isVisibleAttachActionMenu, setIsVisibleAttachActionMenu] = useState(false);
	const [isVisibleExportActionMenu, setIsVisibleExportActionMenu] = useState(false);
	const [isVisibleDeleteActionMenu, setIsVisibleDeleteActionMenu] = useState(false);
	const [isVisibleCommonValuesActionMenu, setIsVisibleCommonValuesActionMenu] = useState(false);
	const [isVisibleImportActionMenu, setIsVisibleImportActionMenu] = useState(false);
	const [isVisibleAssetVisibilityMenu, setIsVisibleAssetVisibilityMenu] = useState(false);

	const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

	const closeAssetVisibilityMenu = () => setIsVisibleAssetVisibilityMenu(false);

	const [isDirtyFilters, setIsDirtyFilters] = useState(false);

	const [isPinnedColumns, setIsPinnedColumns] = useState(true);

	const togglePinColumns = () => setIsPinnedColumns(prevState => !prevState);

	const isPinActionAvailable = (isPinColumns || !isWithoutActions) && hasHorizontalScroll;

	const isGroupOperations = isPinActionAvailable
		|| isToggleAssetVisibility
		|| (customLeftAction && isCustomActionInGroupOperations);

	const tableClassNames = cn(styles.mainTable, className, {
		[styles.checkboxWidth]: !isWithoutCheckbox,
		[styles.loading]: isLoading,
		[styles.checkboxStyle]: !isWithoutCheckbox && isEditAccess,
	});

	const emptyTextClassNames = cn(styles.emptyText, {
		[styles.emptyTextCenter]: isEmptyTextCenter,
	});

	const hiddenColumnsAccessors = useMemo(() => columns.reduce<Record<string, boolean>>((acc, column) => {
		if ((column as any)?.isHidden) {
			const key = (column as any).accessorKey;

			acc[key] = false;
		}
		return acc;
	}, {}), [columns]);

	useOnClickOutside({ refs: [actionExportMenuWrapperRef], handler: () => setIsVisibleExportActionMenu(false) });

	useOnClickOutside({ refs: [actionAttachMenuWrapperRef], handler: () => setIsVisibleAttachActionMenu(false) });

	useOnClickOutside({ refs: [actionDeleteMenuWrapperRef], handler: () => setIsVisibleDeleteActionMenu(false) });

	useOnClickOutside({
		refs: [actionCommonValuesMenuWrapperRef],
		handler: () => setIsVisibleCommonValuesActionMenu(false),
	});

	useOnClickOutside({ refs: [actionImportMenuWrapperRef], handler: () => setIsVisibleImportActionMenu(false) });

	useOnClickOutside({ refs: [actionAssetVisibilityMenuWrapperRef], handler: closeAssetVisibilityMenu });

	useEffect(() => {
		if (isOpenFilters !== null) {
			setIsShowFilters(isOpenFilters);
		}
	}, [tableName, isOpenFilters]);

	useEffect(() => {
		const scrollWrapperElement = scrollWrapperRef.current;

		if (!scrollWrapperElement) {
			return;
		}

		const checkHorizontalScroll = () => {
			setHasHorizontalScroll(scrollWrapperElement.scrollWidth > scrollWrapperElement.clientWidth);
		};

		checkHorizontalScroll();

		const resizeObserver = new ResizeObserver(checkHorizontalScroll);
		resizeObserver.observe(scrollWrapperElement);

		window.addEventListener('resize', checkHorizontalScroll);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', checkHorizontalScroll);
		};
	}, [data]);

	useEffect(() => {
		if (!hasHorizontalScroll) {
			setIsPinnedColumns(false);
		} else {
			setIsPinnedColumns(true);
		}
	}, [hasHorizontalScroll]);

	useEffect(() => {
		if (!stickyLeftColumnIndexes?.length || isLoading || isEmpty(data)) return;

		const applyStickyOffsets = () => {
			const stickyColumnsLength = stickyLeftColumnIndexes.length;
			const stickyColumns: HTMLElement[] = [];
			const allStickyCells: NodeListOf<HTMLElement>[] = [];

			for (let i = 0; i < stickyColumnsLength; i++) {
				const column = tableRef.current?.querySelector<HTMLElement>(`thead tr th:nth-child(${i + 1})`);
				const cells = tableRef.current?.querySelectorAll<HTMLElement>(`tbody tr td:nth-child(${i + 1})`);

				if (column) stickyColumns.push(column);
				if (cells) allStickyCells.push(cells);
			}

			let leftOffset = 0;

			for (let i = 0; i < stickyColumns.length; i++) {
				const col = stickyColumns[i];
				const cellGroup = allStickyCells[i];

				col.style.left = `${leftOffset}px`;
				cellGroup.forEach(cell => {
					cell.style.left = `${leftOffset}px`;
				});

				leftOffset += col.offsetWidth;
			}
		};

		applyStickyOffsets();

		const resizeObserver = new ResizeObserver(() => {
			applyStickyOffsets();
		});

		if (scrollWrapperRef.current) {
			resizeObserver.observe(scrollWrapperRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [data, stickyLeftColumnIndexes?.length, isLoading, isPinnedColumns]);

	const {
		getHeaderGroups,
		getRowModel,
		getState,
		resetRowSelection,
		toggleAllRowsExpanded,
	} = useReactTable({
		data,
		columns: finalColumns,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		onSortingChange: setSorting,
		enableRowSelection: row => !row?.original?.isSelectRowDisabled,
		getSubRows: row => row.subRows,
		onExpandedChange: setExpanded,
		getRowId: isDragAndDrop ? getRowId : undefined,
		initialState: {
			columnVisibility: hiddenColumnsAccessors,
		},
		state: {
			sorting,
			columnVisibility: hiddenColumnsAccessors,
			expanded,
		},
		sortDescFirst: true,
	});

	const { rowSelection: selectedRowIds } = getState();
	const { rows, flatRows: selectedFlatRows } = getRowModel();

	const rowsToCollapse = useMemo(() => {
		return Object
			.values(rows)
			.filter((row) => row.getCanExpand() && row.getIsExpanded());
	}, [rows]);

	const isSelectedSortIdShouldCollapse = useMemo(() => {
		return sortId?.id && collapseTriggerNames.includes(sortId.id);
	}, [sortId, collapseTriggerNames]);

	const closeExpandedRows = useCallback(() => {
		if (isEmpty(rowsToCollapse)) return;

		toggleAllRowsExpanded(false);
	}, [rowsToCollapse, toggleAllRowsExpanded]);

	useEffect(() => {
		if (isSelectedSortIdShouldCollapse) closeExpandedRows();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSelectedSortIdShouldCollapse && sortId]);

	useEffect(() => {
		if (!isEmpty(expanded)) closeExpandedRows();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination]);

	useEffect(() => {
		sort.handleSort(sorting);
	}, [sort, sorting]);

	const toggleFilters = () => setIsShowFilters(prevState => !prevState);

	const toggleVisibleExportActionMenu = () => {
		if (!data.length) {
			return;
		}

		const ids = getSelectedIds();

		if (setCheckedIds) {
			setCheckedIds(ids);
		}

		return handleCustomExport
			? handleCustomExport()
			: setIsVisibleExportActionMenu(prevState => !prevState);
	};

	const toggleVisibleDeleteActionMenu = () => {
		if (!data.length || isDeleteActionDisabled) return;

		return toggleDeleteModal || isWithoutDeleteActionMenu
			? handleDelete()
			: setIsVisibleDeleteActionMenu(prevState => !prevState);
	};

	const toggleVisibleCommonValuesActionMenu = () => {
		if (!data.length) return;

		setIsVisibleCommonValuesActionMenu(prevState => !prevState);
	};

	const toggleVisibleAttachActionMenu = () => {
		if (isAttachDisabled) return;

		setIsVisibleAttachActionMenu(prevState => !prevState);
	};

	const toggleVisibleImportActionMenu = () => setIsVisibleImportActionMenu(prevState => !prevState);

	const toggleVisibleAssetVisibilityMenu = () => {
		if (isEmptyData) return;

		setIsVisibleAssetVisibilityMenu(prevState => !prevState);
	};

	const resetFilterHandler = () => {
		if (isDisabledClearFilters) return;

		if (handleResetFilters) {
			handleResetFilters();
		} else {
			filters?.forEach(({ setState }) => {
				setState(null);
			});

			dateFilters?.forEach(({ handleChange }) => {
				handleChange(null);
			});
		}

		if (multiDataFilters?.clearSelectedFilters) {
			multiDataFilters.clearSelectedFilters();
		}

		toggleFilters();
	};

	const flattenRows = (rows: Row<any>[], selectedOnly?: boolean) => {
		const selectedIds: string[] = Object.keys(selectedRowIds);

		return rows.reduce<Row<any>[]>((acc, row) => {
			if (row.getCanExpand() && !row.getIsExpanded() && row.subRows?.length) {
				const subRows = selectedOnly
					? row.subRows.filter((subRow) => selectedIds.includes(subRow.id))
					: row.subRows;

				acc.push(...subRows);
			} else if (!selectedOnly || selectedOnly && row.getIsSelected()) {
				acc.push(row);
			}

			return acc;
		}, []);
	};

	const getFolderCheckStatuses = (rows: Row<any>[]): TFoldersId[] => {
		const selectedFolderIds = new Set(Object.keys(selectedRowIds).filter(id => !id.includes('.')));

		return rows.reduce<TFoldersId[]>((folderStatuses, row) => {
			const folderId = row.original?.folderId;

			if (!folderId) {
				return folderStatuses;
			}

			if (row.getCanExpand() && row.subRows?.length) {
				const allSubRowsChecked = row.subRows.every(subRow => subRow.getIsSelected());

				folderStatuses.push({ folderId, allChecked: allSubRowsChecked });
			} else if (!row.subRows?.length && selectedFolderIds.has(row.id)) {
				folderStatuses.push({ folderId, allChecked: true });
			} else if (!row.subRows?.length) {
				folderStatuses.push({ folderId, allChecked: false });
			}

			return folderStatuses;
		}, []);
	};

	const onClickExportMenuItem = (actionId: TMenuActionsTypes) => {
		const ids = getIdsGroupAction({
			actionId,
			selectedFlatRows: flattenRows(rows, true),
			dispatch,
			rows: flattenRows(rows),
			keysForCheckedIds,
		});

		if (handleExport) handleExport(ids, actionId);
	};

	const onClickAttachMenuItem = (actionId: TMenuActionsTypes) => {
		const ids = getIdsGroupAction({
			actionId,
			selectedFlatRows: flattenRows(rows, true),
			dispatch,
			rows: flattenRows(rows),
			keysForCheckedIds,
		});

		if (handleAttach) {
			handleAttach({ ids, actionType: actionId });
		}
	};

	const onClickMoveItems = () => {
		const ids = getIdsGroupAction({
			actionId: EMenuActionsTypes.selected,
			selectedFlatRows: flattenRows(rows, true),
			dispatch,
			rows: flattenRows(rows),
			keysForCheckedIds,
		});

		if (handleMoveItems) {
			handleMoveItems({ ids });
		}
	};

	const onClickDeleteMenuItem = (actionId: TMenuActionsTypes) => {
		if (!handleCustomDelete) return;

		const ids = getIdsGroupAction({
			actionId,
			selectedFlatRows: flattenRows(rows, true),
			dispatch,
			rows: flattenRows(rows),
			keysForCheckedIds,
		});

		const folderIds = getFolderCheckStatuses(rows);

		handleCustomDelete({ ids, folderIds, actionType: actionId });
	};

	const onClickCommonValuesMenuItem = (actionId: TMenuActionsTypes) => {
		const ids = getIdsGroupAction({
			actionId,
			selectedFlatRows: flattenRows(rows, true),
			dispatch,
			rows,
			keysForCheckedIds,
		});

		if (handleChangeCommonValues) handleChangeCommonValues(ids, actionId);
	};

	const onClickImportMenuItem = (actionId: string) => handleImport && handleImport(actionId);

	useEffect(() => {
		if (!filters?.length) return;

		filters.forEach(({ setState, defaultValue, options, type }) => {
			if (defaultValue !== null) {
				if (type === TableFilterTypes.MULTISELECT) {
					const defaultValues = Array.isArray(defaultValue)
						? options.filter(option => defaultValue.some(value => value === option?.value))
						: null;

					setState(defaultValues);
				} else {
					setState(options?.find(option => option?.value === defaultValue) || null);
				}
			}
		});

		dateFilters.forEach(({ selectProps }) => {
			if (!selectProps) return;

			const { defaultValue, setState, options } = selectProps;

			if (defaultValue !== null) {
				setState(options?.find(option => option?.value === defaultValue) || null);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const isDirtyFilters = filters?.some((filter) => {
			if (filter?.type === TableFilterTypes.MULTISELECT) {
				return Array.isArray(filter.option) && filter.option.length;
			}

			return filter.option;
		}) as boolean;

		const isDirtyDateFilters = dateFilters?.some((filter) => filter.selected) as boolean;

		setIsDirtyFilters(isDirtyFilters || isDirtyDateFilters);
	}, [filters, dateFilters]);

	const getSelectedIds = useCallback(() => {
		const selectedIndexes = Object.keys(selectedRowIds).map((index) => parseInt(index));

		return data.reduce<Array<string>>((acc, item, index) => {
			if (selectedIndexes.includes(index)) {
				acc.push(item[keysForCheckedIds[0]]);
			}

			return acc;
		}, []);
	}, [data, selectedRowIds, keysForCheckedIds]);

	const takeActionOnSelectedCells = ({
		openModal,
		isAdminCheckRequired = false,
	}: {
		openModal?: ((isAdmin?: boolean) => void);
		isAdminCheckRequired?: boolean;
	}) => {
		if (isEmpty(data) || isEmpty(selectedRowIds)) return;

		const ids = getSelectedIds();

		const isAdminsExistInSelection = isAdminCheckRequired
			? data.some(item => {
				return ((checkIsAdmin(item?.roles?.map((role: TRole) => role.name))
					|| checkIsPlatformOwner(item?.roles?.map((role: TRole) => role.name))) && ids.includes(item.id));
			})
			: false;

		if (setCheckedIds) setCheckedIds(ids);
		if (openModal) openModal(isAdminsExistInSelection);
	};

	const openAddModal = () => {
		if (toggleAddModal) toggleAddModal();
	};

	const handleVerifyCoordinates = () => {
		if (!verifyCoordinates || isEmpty(data) || isEmpty(selectedRowIds)) return;

		verifyCoordinates(getSelectedIds());
	};

	const handleUnlink = () => takeActionOnSelectedCells({ openModal: toggleUnlinkModal });
	const handleDelete = () => takeActionOnSelectedCells({ openModal: toggleDeleteModal, isAdminCheckRequired: true });

	const exportActions = useMemo(() => {
		const exportActionsBasedOnSelection = modifyMenuActionsBasedOnSelection(customExportActions, selectedRowIds);

		const exportActionsBasedOnFilters = modifyMenuActionsBasedOnFilters(
			exportActionsBasedOnSelection,
			isDirtyFilters || !!search?.some((item) => item.initialValue),
		);

		return modifyMenuActionsTextBasedOnCount(exportActionsBasedOnFilters);
	}, [
		selectedRowIds,
		customExportActions,
		isDirtyFilters,
		search,
	]);

	const removeActions = useMemo(() => {
		const removeActionsBasedOnSelection = modifyMenuActionsBasedOnSelection(customRemoveActions, selectedRowIds);

		return modifyMenuActionsBasedOnFilters(
			removeActionsBasedOnSelection,
			isDirtyFilters || !!search?.some((item) => item.initialValue));
	}, [
		selectedRowIds,
		customRemoveActions,
		isDirtyFilters,
		search,
	]);

	const valuesActions = useMemo(() => {
		const valuesActionsBasedOnSelection = modifyMenuActionsBasedOnSelection(commonValuesActions, selectedRowIds);

		return modifyMenuActionsBasedOnFilters(valuesActionsBasedOnSelection, isDirtyFilters);
	}, [selectedRowIds, commonValuesActions, isDirtyFilters]);

	const attachActions = useMemo(() => {
		const attachActionsBasedOnSelection = modifyMenuActionsBasedOnSelection(customAttachActions, selectedRowIds);

		return modifyMenuActionsBasedOnFilters(
			attachActionsBasedOnSelection,
			isDirtyFilters || !!search?.some((item) => item.initialValue),
		);
	}, [selectedRowIds, customAttachActions, isDirtyFilters, search]);

	const scrollToTop = () => {
		if (scrollWrapperRef.current) {
			scrollWrapperRef.current.scrollTop = 0;
		}
	};

	useEffect(() => {
		if (!isWithoutCheckbox && isWithPagination) resetRowSelection();
	}, [
		pagination,
		resetRowSelection,
		isWithoutCheckbox,
		isWithPagination,
	]);

	const selectedIds = useMemo(() => getSelectedIds(), [getSelectedIds]);

	useEffect(() => {
		if (setSelectedIds) {
			setSelectedIds(selectedIds);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setSelectedIds, selectedIds.length]);

	const isDeleteDisabled = isDeleteActionDisabled
		|| (
			toggleDeleteModal ? !selectedFlatRows.length : !data?.length
		);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;

		if (!over || !onRowReorder) {
			return;
		}

		if (active.id !== over.id) {
			const oldIndex = data.findIndex(item => getRowId(item, 0) === active.id);
			const newIndex = data.findIndex(item => getRowId(item, 0) === over.id);

			if (oldIndex !== -1 && newIndex !== -1) {
				onRowReorder(oldIndex, newIndex);
			}
		}
	}, [data, onRowReorder, getRowId]);

	const rowIds = useMemo(() => {
		return data.map((item, index) => getRowId(item, index));
	}, [data, getRowId]);

	return (
		<div
			className={styles.tableContainer}
			style={{ maxHeight, height }}
		>
			<div className={styles.tableActions}>
				<div className={styles.leftActions}>
					{search && (
						<MultiSearch
							disabled={isSearchDisabled}
							isOpenSearch={isOpenSearch}
							search={search}
							searchThreshold={searchThreshold}
							searchWidth={searchWidth}
						/>
					)}
					{!isWithoutFilter
						&& <Tooltip content={filterTooltipText}>
							<div
								className={styles.icon}
								onClick={toggleFilters}
							>
								<DynamicIconWrapper
									icon={Icons.filter}
									isActive={!!isShowFilters}
									isDirty={isDirtyFilters && !isShowFilters}
								/>
							</div>
						</Tooltip>
					}
					{isEditAccess && !isWithoutDeleteAction
						&& <div
							ref={actionDeleteMenuWrapperRef}
							className={styles.relative}
							onClick={toggleVisibleDeleteActionMenu}
						>
							<Tooltip content={deleteSelectedTooltipText}>
								<div className={styles.icon}>
									<DynamicIconWrapper
										icon={Icons.delete}
										isDisabled={isDeleteDisabled}
									/>
								</div>
							</Tooltip>
							<ActionsMenu
								actions={removeActions}
								isVisibleMenu={isVisibleDeleteActionMenu}
								minWidth={EXPORT_MENU_MIN_WIDTH}
								onClick={onClickDeleteMenuItem}
							/>
						</div>
					}
					{isLinkAccess && isLink
						&& <Tooltip content={unlinkSelectedTooltipText}>
							<div
								className={styles.icon}
								onClick={handleUnlink}
							>
								<DynamicIconWrapper
									icon={Icons.unlink}
									isDisabled={!selectedFlatRows.length}
								/>
							</div>
						</Tooltip>
					}
					{isLinkAccess && isVerifyCoordinates
						&& <Tooltip content={verifyCoordinatesSelectedTooltipText}>
							<div
								className={styles.icon}
								onClick={handleVerifyCoordinates}
							>
								<DynamicIconWrapper
									icon={Icons.verifyCoordinates}
									isDisabled={!selectedFlatRows.length}
								/>
							</div>
						</Tooltip>
					}
					{isExportSettings && (
						<Tooltip content={setExportRulesTooltipText}>
							<div
								className={styles.iconSetting}
								onClick={handleExportRules}
							>
								<DynamicIconWrapper icon={Icons.settingsPainted} />
							</div>
						</Tooltip>
					)}
					{isExport && (
						<div
							ref={actionExportMenuWrapperRef}
							className={styles.relative}
							onClick={toggleVisibleExportActionMenu}
						>
							<Tooltip content={exportTooltipText || exportTooltipDefaultText}>
								<div className={styles.icon}>
									<DynamicIconWrapper
										icon={Icons.export}
										isDisabled={!data?.length}
									/>
								</div>
							</Tooltip>
							<ActionsMenu
								actions={exportActions}
								isVisibleMenu={isVisibleExportActionMenu}
								minWidth={EXPORT_MENU_MIN_WIDTH}
								onClick={onClickExportMenuItem}
							/>
						</div>
					)}
					{isMoveItems && (
						<Tooltip content={moveItemsTooltipText}>
							<div
								className={styles.icon}
								onClick={onClickMoveItems}
							>
								<DynamicIconWrapper
									icon={Icons.convertIcon}
									isDisabled={isEmpty(selectedRowIds)}
								/>
							</div>
						</Tooltip>
					)}
					{isAttach && (
						<div
							ref={actionAttachMenuWrapperRef}
							className={styles.relative}
							onClick={toggleVisibleAttachActionMenu}
						>
							<Tooltip content={attachTooltipText}>
								<div className={styles.icon}>
									<DynamicIconWrapper
										icon={Icons.attachIcon}
										isDisabled={isAttachDisabled}
									/>
								</div>
							</Tooltip>
							<ActionsMenu
								actions={attachActions}
								isVisibleMenu={isVisibleAttachActionMenu}
								minWidth={EXPORT_MENU_MIN_WIDTH}
								onClick={onClickAttachMenuItem}
							/>
						</div>
					)}
					{isEditAccess && isImportable
						&& <div
							ref={actionImportMenuWrapperRef}
							className={styles.relative}
							onClick={isImportSettings ? toggleVisibleImportActionMenu : undefined}
						>
							<Tooltip content={importTooltipText}>
								<div
									className={styles.icon}
									onClick={!isImportSettings ? (handleImport as () => void) : undefined}
								>
									<DynamicIconWrapper icon={Icons.import} />
								</div>
							</Tooltip>
							<ActionsMenu
								actions={importSitesActions}
								isVisibleMenu={isVisibleImportActionMenu}
								minWidth={EXPORT_MENU_MIN_WIDTH}
								onClick={onClickImportMenuItem}
							/>
						</div>
					}
					{isGroupOperations && <div className={styles.separator} />}
					{isPinActionAvailable && (
						<>
							<Tooltip content={isPinnedColumns ? unpinColumns : pinColumns}>
								<div
									className={styles.icon}
									onClick={togglePinColumns}
								>
									<DynamicIconWrapper icon={isPinnedColumns ? Icons.unpin : Icons.pin} />
								</div>
							</Tooltip>
						</>
					)}
					{isToggleAssetVisibility && handleVisibilityAction && (
						<div
							ref={actionAssetVisibilityMenuWrapperRef}
							className={styles.relative}
							onClick={toggleVisibleAssetVisibilityMenu}
						>
							<Tooltip content={assetVisibilityTooltipText}>
								<div className={styles.icon}>
									<DynamicIconWrapper
										icon={Icons.commonLocationIcon}
										isDisabled={isEmptyData}
									/>
								</div>
							</Tooltip>
							<AssetVisibilityActionsMenu
								handleVisibilityAction={handleVisibilityAction}
								isDirtyFilters={isDirtyFilters}
								isVisibleMenu={isVisibleAssetVisibilityMenu}
								rows={rows as Row<TAsset>[]}
								selectedIds={getSelectedIds()}
							/>
						</div>
					)}
					{isSetCommonValues && (
						<div
							ref={actionCommonValuesMenuWrapperRef}
							className={styles.relative}
							onClick={toggleVisibleCommonValuesActionMenu}
						>
							<Tooltip content={setCommonValuesTooltipText}>
								<div className={styles.icon}>
									<DynamicIconWrapper icon={Icons.slidersIcon} />
								</div>
							</Tooltip>
							<ActionsMenu
								actions={valuesActions}
								isVisibleMenu={isVisibleCommonValuesActionMenu}
								minWidth={EXPORT_MENU_MIN_WIDTH}
								onClick={onClickCommonValuesMenuItem}
							/>
						</div>
					)}
					{isRefreshTable && (
						<>
							<Tooltip content={refreshAction}>
								<div
									className={styles.icon}
									onClick={handleRefresh}
								>
									<DynamicIconWrapper icon={Refresh} />
								</div>
							</Tooltip>
						</>
					)}
					{customLeftAction && (customLeftAction)}
				</div>
				{isEditAccess && isCreatable && (
					<Tooltip
						content={addTooltipText}
						isDisabled={!addTooltipText}
					>
						<div className={styles.rightActions}>
							<Button
								disabled={isAddDisabled}
								icon={addIcon()}
								onClick={openAddModal}
								secondary
								text={addText}
							/>
						</div>
					</Tooltip>
				)}
				{customRightAction && (customRightAction)}
			</div>
			{isShowFilters &&
				<div className={styles.tableFilters}>
					{!!multiDataFilters && <MultiDataFilter multiDataFilters={multiDataFilters}/>}
					{filters?.filter(item => !item?.isHidden)?.map(({
						disabled = false,
						name,
						placeholder,
						option,
						options,
						width,
						minWidth,
						maxWidth,
						maxMenuHeight,
						setState,
						isFilterStyle = true,
						type,
					}) => {
						if (type === TableFilterTypes.MULTISELECT) {
							const onChange = (selectedOptions: TSelectOption[]) => {
								setState(selectedOptions);
							};

							return (
								<React.Fragment key={name}>
									<Select
										disabled={disabled}
										handleChange={onChange}
										isClearable={isFilterStyle}
										isFilterStyle={isFilterStyle}
										isMulti
										isWithoutUseForms
										maxMenuHeight={maxMenuHeight}
										minWidth={minWidth}
										name={name}
										options={options}
										placeholder={placeholder}
										value={option}
										width={width}
									/>
								</React.Fragment>
							);
						}

						const onChange = (selectedOption: TSelectOption) => {
							setState(selectedOption);
						};

						return (
							<React.Fragment key={name}>
								<Select
									customComponents={{ Option: OptionTruncationOnly }}
									disabled={disabled}
									handleChange={onChange}
									isClearable={isFilterStyle}
									isFilterStyle={isFilterStyle}
									isWithoutUseForms
									maxMenuHeight={maxMenuHeight}
									maxWidth={maxWidth}
									minWidth={minWidth}
									name={name}
									options={options}
									placeholder={placeholder}
									value={option}
									width={width}
								/>
							</React.Fragment>
						);
					})
					}
					{!!dateFilters && dateFilters?.map(({
						label,
						handleChange,
						selected,
						name,
						minDate,
						maxDate,
						disableFutureDates,
						selectProps,
						includeDatesFrom,
					}) => {
						const onChange = (selectedOption: TSelectOption | unknown) => {
							selectProps?.setState(selectedOption as TSelectOption);
						};

						return (
							<div
								key={name}
								className={styles.dateFilter}
							>
								{label && selectProps
									&& <Label
										className={styles.selectDatepickerLabel}
										label={label}
									/>
								}
								{selectProps
									&& <Select
										className={styles.select}
										handleChange={onChange}
										isFilter
										isWithoutUseForms
										maxWidth={selectProps?.maxWidth}
										minWidth={selectProps?.minWidth}
										name={selectProps?.name}
										options={selectProps.options}
										placeholder={selectProps.placeholder}
										value={selectProps.option}
										width={selectProps?.width}
									/>
								}
								<Datepicker
									key={name}
									disableFutureDates={disableFutureDates}
									handleChange={handleChange as never}
									includeDates={includeDatesFrom}
									isClearable={isClearableDatepicker}
									isLeftPlaceholder
									isMobileDevice={false}
									label={!selectProps ? label : undefined}
									maxDate={maxDate}
									minDate={minDate}
									selected={selected}
								/>
							</div>
						);
					})
					}
					<div
						className={cn(styles.resetBtn, { [styles.disabled]: isDisabledClearFilters })}
						onClick={resetFilterHandler}
					>
						<Tooltip content={resetFilterTooltipText}>
							<div>
								Clear filters
							</div>
						</Tooltip>
					</div>
				</div>
			}
			<div
				ref={scrollWrapperRef}
				className={styles.tableScrollWrapper}
				onScroll={onScrollHandler}
			>
				{isDragAndDrop ? (
					<DragDropTableWrapper
						items={rowIds}
						onDragEnd={handleDragEnd}
					>
						<table
							ref={tableRef}
							className={tableClassNames}
						>
							<thead>
							{getHeaderGroups().map((headerGroup, i) => {
								const stickyColumns = headerGroup.headers.filter((_, k) =>
									stickyLeftColumnIndexes?.includes(k));

								const notStickyColumns = isPinnedColumns
									? headerGroup.headers.filter((_, k) => !stickyLeftColumnIndexes?.includes(k))
									: headerGroup.headers;

								return (
									<tr
										key={`thead-tr-${i}`}
										ref={tHeadTrRef}
									>
										{!isEmpty(stickyColumns) && isPinnedColumns
											&& stickyColumns.map((header: any, j) => {
												return (
													<th
														key={`thead-th-${j}`}
														className={cn('table-headerCell', styles.stickyCellLeft, {
															[styles.checkboxCell]: header?.id === 'selection',
															[styles.dragHandleCell]:
																header?.id === DRAG_HANDLE_COLUMN_ID,
														})}
														data-testid={`thead-th-${j}`}
														style={{ width: header.cellWidth }}
													>
														<div
															className={styles.header}
															onClick={header.column.getToggleSortingHandler()}
														>
															{/* eslint-disable-next-line max-len */}
															{flexRender(header.column.columnDef.header, header.getContext())}
															{header.column.getCanSort()
																&& <div className={styles.triangleIcon}>
																	{header.column.getIsSorted()
																		? header.column.getNextSortingOrder() === 'asc'
																			? Icons.triangleDown()
																			: Icons.triangleUp()
																		: Icons.triangleDown({ color: colors.indigo20 })
																	}
																</div>
															}
														</div>
													</th>
												);
											})
										}
										{notStickyColumns.map((header: any, j, elements) => {
											return (
												<th
													key={`thead-th-${j}`}
													className={cn('table-headerCell', {
														[styles.stickyCellRight]: !isWithoutActions
															&& isPinnedColumns
															&& (elements.length - 1 === j),
														[styles.checkboxCell]: header?.id === 'selection',
														[styles.dragHandleCell]: header?.id === 'dragHandle',
													})}
													data-testid={`thead-th-${j}`}
													style={{ width: header.cellWidth }}
												>
													<div
														className={styles.header}
														onClick={header.column.getToggleSortingHandler()}
													>
														{/* eslint-disable-next-line max-len */}
														{flexRender(header.column.columnDef.header, header.getContext())}
														{header.column.getCanSort()
															&& <div className={styles.triangleIcon}>
																{header.column.getIsSorted()
																	? header.column.getNextSortingOrder() === 'asc'
																		? Icons.triangleDown()
																		: Icons.triangleUp()
																	: Icons.triangleDown({ color: colors.indigo20 })
																}
															</div>
														}
													</div>
												</th>
											);
										})}
									</tr>
								);
							})}
							</thead>
							<tbody>
							{getRowModel().rows.map((row: Row<any>, i) => (
								<SortableRow
									key={row.id}
									dragHandle={dragHandle}
									index={i}
									isDragDisabled={isLoading}
									isPinnedColumns={isPinnedColumns}
									isWithoutActions={isWithoutActions}
									row={row}
									stickyLeftColumnIndexes={stickyLeftColumnIndexes}
								/>
							))}
							{isLoading
								&& <tr className={styles.spinnerContainer}>
									<td>
										{LoadingComponent
											? LoadingComponent
											: <SpinnerOverlay />
										}
									</td>
								</tr>
							}
							</tbody>
						</table>
					</DragDropTableWrapper>
				) : (
					<table
						ref={tableRef}
						className={tableClassNames}
					>
						<thead>
						{getHeaderGroups().map((headerGroup, i) => {
							const stickyColumns = headerGroup.headers.filter((_, k) =>
								stickyLeftColumnIndexes?.includes(k));

							const notStickyColumns = isPinnedColumns
								? headerGroup.headers.filter((_, k) => !stickyLeftColumnIndexes?.includes(k))
								: headerGroup.headers;

							return (
								<tr
									key={`thead-tr-${i}`}
									ref={tHeadTrRef}
								>
									{!isEmpty(stickyColumns) && isPinnedColumns
										&& stickyColumns.map((header: any, j) => {
											return (
												<th
													key={`thead-th-${j}`}
													className={cn('table-headerCell', styles.stickyCellLeft, {
														[styles.checkboxCell]: header?.id === 'selection',
														[styles.dragHandleCell]: header?.id === 'dragHandle',
													})}
													data-testid={`thead-th-${j}`}
													style={{ width: header.cellWidth }}
												>
													<div
														className={styles.header}
														onClick={header.column.getToggleSortingHandler()}
													>
														{/* eslint-disable-next-line max-len */}
														{flexRender(header.column.columnDef.header, header.getContext())}
														{header.column.getCanSort()
															&& <div className={styles.triangleIcon}>
																{header.column.getIsSorted()
																	? header.column.getNextSortingOrder() === 'asc'
																		? Icons.triangleDown()
																		: Icons.triangleUp()
																	: Icons.triangleDown({ color: colors.indigo20 })
																}
															</div>
														}
													</div>
												</th>
											);
										})
									}
									{notStickyColumns.map((header: any, j, elements) => {
										return (
											<th
												key={`thead-th-${j}`}
												className={cn('table-headerCell', {
													[styles.stickyCellRight]: !isWithoutActions
														&& isPinnedColumns
														&& (elements.length - 1 === j),
													[styles.checkboxCell]: header?.id === 'selection',
												})}
												data-testid={`thead-th-${j}`}
												style={{ width: header.cellWidth }}
											>
												<div
													className={styles.header}
													onClick={header.column.getToggleSortingHandler()}
												>
													{flexRender(header.column.columnDef.header, header.getContext())}
													{header.column.getCanSort()
														&& <div className={styles.triangleIcon}>
															{header.column.getIsSorted()
																? header.column.getNextSortingOrder() === 'asc'
																	? Icons.triangleDown()
																	: Icons.triangleUp()
																: Icons.triangleDown({ color: colors.indigo20 })
															}
														</div>
													}
												</div>
											</th>
										);
									})}
								</tr>
							);
						})}
						</thead>
						<tbody>
						{getRowModel().rows.map((row: Row<any>, i) => {
							const cells = row.getVisibleCells();
							const stickyCells = cells.filter((_, k) => stickyLeftColumnIndexes?.includes(k));

							const notStickyCells = isPinnedColumns
								? cells.filter((_, k) => !stickyLeftColumnIndexes?.includes(k))
								: cells;

							const isSubRow = !!row.depth;

							return (
								<tr
									key={`tbody-tr-${i}`}
									ref={tBodyTrRef}
									data-testid={`tbody-tr-${i}`}
								>
									{!isEmpty(stickyCells) && isPinnedColumns
										&& stickyCells.map((cell, j) => {
											return (
												<td
													key={`tbody-td-${j}`}
													className={cn('table-cell', styles.stickyCellLeft)}
												>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</td>
											);
										})
									}
									{notStickyCells.map((cell, j, elements: string | any[]) => {
										return (
											<td
												key={`tbody-td-${j}`}
												className={cn('table-cell', {
													[styles.stickyCellRight]: !isWithoutActions
														&& isPinnedColumns
														&& (elements.length - 1 === j),
													[styles.expandedRow]: isSubRow,
												})}
											>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										);
									})}
								</tr>
							);
						})}
						{isLoading
							&& <tr className={styles.spinnerContainer}>
								<td>
									{LoadingComponent
										? LoadingComponent
										: <SpinnerOverlay />
									}
								</td>
							</tr>
						}
						</tbody>
					</table>
				)}
				{(isWithPagination && !pagination?.countOfRecords && !isLoading && isEmpty(data))
					&& <div className={emptyTextClassNames}>
						{emptyText}
					</div>
				}
			</div>
			{isWithPagination && !!pagination?.countOfRecords
				&& <Pagination
					currentPage={pagination?.forcePage}
					hasCustomOrder={pagination?.hasCustomOrder}
					hideControls={pagination?.withoutPagination}
					onPageChange={pagination?.pageHandler}
					onPageSizeChange={pagination?.pageSizeHandler}
					pageSize={pagination?.size}
					resetOrder={pagination?.resetOrder}
					scrollToTop={scrollToTop}
					totalItems={pagination?.countOfRecords}
				/>
			}
		</div>
	);
};

export default memo(Table);
